import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogIn,
  Eye,
  EyeOff,
} from "lucide-react";
import { loginStaff } from "../api/staffAuthApi";
import {
  Button,
  Input,
  Card,
  CardContent,
} from "../components/ui";
import { useToast } from "../context/ToastContext";

const UserLogin = () => {
  const navigate = useNavigate();

  const [staffCode, setStaffCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await loginStaff(staffCode, password);

      navigate("/staff-home");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-neutral-50 dark:bg-neutral-950">
      {/* Logo */}
      <div className="px-5 py-5 sm:px-8 sm:py-6">
        <a href="/" className="inline-flex items-center">
          <img
            src="https://res.cloudinary.com/hsvfv0h8/image/upload/v1786152037/Untitled_design__6_-removebg-preview_zijzg4.png"
            alt="EduFlow"
            className="h-11 w-auto object-contain sm:h-12"
          />
        </a>
      </div>

      {/* Authentication */}
      <main className="flex justify-center px-5 pb-10 pt-5 sm:px-6 sm:pt-7">
        <div className="w-full max-w-[400px]">
          <Card className="rounded-xl border border-neutral-200 bg-white shadow-none dark:border-neutral-800 dark:bg-neutral-900">
            <CardContent className="p-7 sm:p-8">
              {/* Header */}
              <div className="mb-7">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                  Employee Login
                </h1>

                <p className="mt-1.5 text-sm leading-5 text-neutral-500 dark:text-neutral-400">
                  Sign in with your staff credentials to continue.
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Staff Code */}
                <div className="px-0.5">
                  <Input
                    label="Staff Code"
                    type="text"
                    placeholder="eg. ramesh@school"
                    value={staffCode}
                    onChange={(e) => setStaffCode(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 px-3 text-sm"
                  />
                </div>

                {/* Password */}
                <div className="px-0.5">
                  <Input
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 px-3 pr-10 text-sm"
                    rightIcon={
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword((prev) => !prev)
                        }
                        disabled={loading}
                        className="flex items-center justify-center rounded-md p-1 text-neutral-400 transition-colors hover:text-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-300 disabled:pointer-events-none disabled:opacity-50 dark:text-neutral-500 dark:hover:text-neutral-200 dark:focus:ring-neutral-700"
                        aria-label={
                          showPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    }
                  />
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    isLoading={loading}
                    leftIcon={<LogIn className="h-4 w-4" />}
                    className="h-11 text-sm font-medium"
                  >
                    Sign In
                  </Button>
                </div>
              </form>

              {/* Support */}
              <div className="mt-6 border-t border-neutral-100 dark:border-neutral-800 pt-5 dark:border-neutral-800">
                <p className="text-center text-xs leading-5 text-neutral-400 dark:text-neutral-500">
                  Protected authentication system. For assistance,
                  contact IT support.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UserLogin;